import type { ChurchNameCheck, OnboardingDefaults } from "@ibada/shared-types/onboarding"

import { http } from "@/shared/lib/api/http"


type Envelope<T> = { data: T; meta: Record<string, unknown> }

/** Server geo-IP + Accept-Language suggestion (public, unauthenticated). */
export async function fetchOnboardingDefaults(): Promise<OnboardingDefaults> {
  const response = await http.get<Envelope<OnboardingDefaults>>("/public/onboarding/defaults")
  return response.data.data
}

export type RegisteredChurch = {
  workspaceId: string
  slug: string
  name: string
  status: string
}

/**
 * Register a church for the SIGNED-IN user.
 *
 * Replaces the draft + claim pair. There is nothing to reconcile afterwards because nothing was written
 * before we knew who was writing it — the request carries a session, and the workspace exists when it
 * returns.
 */
export async function registerChurch(input: ChurchRegistrationRequest): Promise<RegisteredChurch> {
  // Registration performs substantial provisioning. It still needs a finite boundary: without one, a stalled
  // upstream keeps React Query pending forever and locks the entire dialog. A timeout is an unknown outcome;
  // the backend remains responsible for making retries safe.
  const response = await http.post<Envelope<RegisteredChurch>>("/workspaces/register", input, {
    timeout: 30_000,
  })
  return response.data.data
}

export type ChurchRegistrationRequest = {
  name: string
  country: string
  language: string
  structureType: string
  creatorRole: string
  phoneNumber?: string
}

/**
 * Is a church already registered under this name, and what is it to the signed-in user?
 *
 * A warning, never a gate — church names repeat across cities, so this catches the double-registration
 * without standing between a real namesake and signing up. The server answers the access half of the
 * question (member / invited / neither), because the client must never decide who belongs where.
 */
export async function checkChurchName(name: string): Promise<ChurchNameCheck> {
  const response = await http.post<Envelope<ChurchNameCheck>>("/workspaces/check-name", {
    name,
  })
  return response.data.data
}
